import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  X,
  FileType,
  Presentation,
  TrendingUp,
  TrendingDown,
  Target,
  CheckCircle,
  Users,
  Calendar,
  DollarSign,
  Layers,
  ArrowRight,
  BarChart3,
  Award,
  Briefcase,
  Clock,
  Building2,
  Sparkles
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area
} from 'recharts';

// Protiviti-inspired professional color palette
const COLORS = {
  primary: '#00579A',      // Protiviti Blue
  primaryDark: '#1B2D4D',  // Dark Blue
  primaryLight: '#37ABE9', // Light Blue
  accent: '#9ED2F3',       // Teal accent
  orange: '#EF7B3A',       // Orange accent
  
  textDark: '#333333',
  textMedium: '#666666',
  textLight: '#999999',
  
  bgLight: '#F5F5F5',
  bgWhite: '#FFFFFF',
  border: '#E0E0E0',
  
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336'
};

const CHART_COLORS = [COLORS.primary, COLORS.primaryLight, COLORS.orange, COLORS.primaryDark, COLORS.accent];

// ==================== PARSING ====================

const parseContentToSlides = (content, mainTitle) => {
  const slides = [];
  let currentSlide = { 
    type: 'cover', 
    title: mainTitle, 
    subtitle: 'Professional Proposal',
    items: [],
    tables: [],
    charts: [],
    kpis: []
  };
  
  const lines = content.split('\n');
  let tableRows = [];
  let inTable = false;
  let sectionCount = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    if (!trimmed) {
      if (inTable && tableRows.length > 0) {
        currentSlide.tables.push([...tableRows]);
        tableRows = [];
        inTable = false;
      }
      continue;
    }
    
    // Section headers
    if (trimmed.startsWith('# ')) {
      if (currentSlide.items.length > 0 || currentSlide.tables.length > 0 || currentSlide.type === 'cover') {
        slides.push(currentSlide);
        sectionCount = 0;
      }
      currentSlide = { 
        type: 'section',
        title: trimmed.slice(2).trim(),
        items: [],
        tables: [],
        charts: [],
        kpis: []
      };
    } else if (trimmed.startsWith('## ')) {
      if (sectionCount >= 5) continue;
      
      if (currentSlide.items.length > 0 || currentSlide.tables.length > 0) {
        slides.push(currentSlide);
        sectionCount++;
      }
      const title = trimmed.slice(3).trim().replace(/^SLIDE \d+:\s*/i, '');
      currentSlide = { 
        type: 'content',
        title: title,
        items: [],
        tables: [],
        charts: [],
        kpis: [],
        layout: detectLayout(title)
      };
    } else if (trimmed.startsWith('### ')) {
      currentSlide.items.push({ 
        type: 'subheading', 
        text: trimmed.slice(4).trim() 
      });
    } else if (trimmed.startsWith('|')) {
      inTable = true;
      const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
      if (!cells.every(c => /^[-:]+$/.test(c)) && cells.length > 0) {
        tableRows.push(cells);
      }
    } else if (trimmed.startsWith('[CHART:')) {
      const chartDesc = trimmed.replace(/^\[CHART:\s*/, '').replace(/\]$/, '');
      currentSlide.charts.push(chartDesc);
    } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      const text = trimmed.replace(/\*\*/g, '');
      if (text.includes(':')) {
        const [label, value] = text.split(':').map(s => s.trim());
        if (value && /\$|%|\d/.test(value)) {
          currentSlide.kpis.push({ label, value });
        } else {
          currentSlide.items.push({ type: 'label', text });
        }
      } else {
        currentSlide.items.push({ type: 'label', text });
      }
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      const text = cleanMarkdown(trimmed.slice(2));
      const metrics = extractMetrics(text);
      currentSlide.items.push({ 
        type: 'bullet', 
        text,
        level: 0,
        metrics,
        isKeyPoint: metrics.value !== null || text.includes(':')
      });
    } else if (trimmed.startsWith('  - ')) {
      currentSlide.items.push({ 
        type: 'bullet', 
        text: cleanMarkdown(trimmed.slice(4)),
        level: 1
      });
    } else if (trimmed.startsWith('>')) {
      currentSlide.items.push({ 
        type: 'quote', 
        text: cleanMarkdown(trimmed.slice(1).trim())
      });
    } else if (trimmed.length > 10) {
      const cleaned = cleanMarkdown(trimmed);
      const metrics = extractMetrics(cleaned);
      if (metrics.value) {
        currentSlide.items.push({ type: 'metric', text: cleaned, metrics });
      } else {
        currentSlide.items.push({ type: 'text', text: cleaned });
      }
    }
  }
  
  if (tableRows.length > 0) {
    currentSlide.tables.push(tableRows);
  }
  
  if (currentSlide.items.length > 0 || currentSlide.tables.length > 0 || slides.length === 0) {
    slides.push(currentSlide);
  }
  
  return slides.slice(0, 25);
};

const cleanMarkdown = (text) => {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1');
};

const extractMetrics = (text) => {
  let value = null;
  let trend = null;
  
  const percentMatch = text.match(/(\d+(?:\.\d+)?)\s*%/);
  const dollarMatch = text.match(/\$\s*([\d,]+(?:\.\d+)?)\s*(million|M|billion|B|K)?/i);
  const multiplierMatch = text.match(/(\d+(?:\.\d+)?)\s*x/i);
  
  if (dollarMatch) {
    const suffix = dollarMatch[2] ? dollarMatch[2][0].toUpperCase() : '';
    value = `$${dollarMatch[1]}${suffix}`;
  } else if (percentMatch) {
    value = `${percentMatch[1]}%`;
  } else if (multiplierMatch) {
    value = `${multiplierMatch[1]}x`;
  }
  
  if (text.toLowerCase().match(/increase|growth|improvement|gain/)) trend = 'up';
  if (text.toLowerCase().match(/decrease|reduction|saving|decline/)) trend = 'down';
  
  return { value, trend, hasNumber: /\d/.test(text) };
};

const detectLayout = (title) => {
  const lower = title.toLowerCase();
  if (lower.match(/execution guideline|governance|quality assurance|communication protocol/)) return 'execution';
  if (lower.match(/summary|overview|recommendation/)) return 'executive';
  if (lower.match(/matrix|analysis|breakdown|requirement/)) return 'table';
  if (lower.match(/timeline|roadmap|phase|approach/)) return 'timeline';
  if (lower.match(/team|structure|organization/)) return 'team';
  if (lower.match(/investment|pricing|cost|commercial/)) return 'financial';
  if (lower.match(/risk|mitigation/)) return 'risk';
  if (lower.match(/kpi|metric|outcome/)) return 'kpi';
  return 'default';
};

// ==================== CHART COMPONENTS ====================

const generateChartData = (desc) => {
  const lower = desc.toLowerCase();
  
  if (lower.match(/phase|timeline|waterfall/)) {
    return [
      { name: 'Phase 1', value: 25, fill: COLORS.primary },
      { name: 'Phase 2', value: 35, fill: COLORS.primaryLight },
      { name: 'Phase 3', value: 25, fill: COLORS.orange },
      { name: 'Phase 4', value: 15, fill: COLORS.primaryDark }
    ];
  }
  
  if (lower.match(/comparison|vs|competitor/)) {
    return [
      { name: 'Capability 1', us: 90, them: 60 },
      { name: 'Capability 2', us: 85, them: 70 },
      { name: 'Capability 3', us: 95, them: 55 },
      { name: 'Capability 4', us: 80, them: 65 }
    ];
  }
  
  if (lower.match(/radar|spider|capability/)) {
    return [
      { subject: 'Strategy', A: 90, B: 60, fullMark: 100 },
      { subject: 'Execution', A: 85, B: 65, fullMark: 100 },
      { subject: 'Analytics', A: 95, B: 55, fullMark: 100 },
      { subject: 'Change Mgmt', A: 80, B: 70, fullMark: 100 },
      { subject: 'Technology', A: 75, B: 80, fullMark: 100 }
    ];
  }
  
  if (lower.match(/pie|distribution/)) {
    return [
      { name: 'Phase 1', value: 20 },
      { name: 'Phase 2', value: 35 },
      { name: 'Phase 3', value: 30 },
      { name: 'Phase 4', value: 15 }
    ];
  }
  
  return [
    { name: 'Q1', value: 65, target: 60 },
    { name: 'Q2', value: 78, target: 70 },
    { name: 'Q3', value: 85, target: 80 },
    { name: 'Q4', value: 92, target: 90 }
  ];
};

const SmartChart = ({ description, height = 160 }) => {
  const lower = description.toLowerCase();
  const data = generateChartData(description);
  
  if (lower.match(/pie|distribution/)) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="35%"
            outerRadius="70%"
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  
  if (lower.match(/radar|spider|capability/)) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart cx="50%" cy="50%" outerRadius="65%" data={data}>
          <PolarGrid stroke={COLORS.border} />
          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: COLORS.textMedium }} />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
          <Radar name="Our Team" dataKey="A" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.4} />
          <Radar name="Competitor" dataKey="B" stroke={COLORS.textLight} fill={COLORS.textLight} fillOpacity={0.2} />
          <Legend wrapperStyle={{ fontSize: '9px' }} />
        </RadarChart>
      </ResponsiveContainer>
    );
  }
  
  if (lower.match(/line|trend/)) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: COLORS.textMedium }} />
          <YAxis tick={{ fontSize: 9, fill: COLORS.textMedium }} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke={COLORS.primary} strokeWidth={2} dot={{ fill: COLORS.primary }} />
          {data[0]?.target !== undefined && (
            <Line type="monotone" dataKey="target" stroke={COLORS.primaryLight} strokeWidth={2} strokeDasharray="5 5" />
          )}
          <Legend wrapperStyle={{ fontSize: '9px' }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  
  // Default: Bar chart
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
        <XAxis dataKey="name" tick={{ fontSize: 9, fill: COLORS.textMedium }} />
        <YAxis tick={{ fontSize: 9, fill: COLORS.textMedium }} />
        <Tooltip />
        {data[0]?.us !== undefined ? (
          <>
            <Bar dataKey="us" name="Our Team" fill={COLORS.primary} radius={[3, 3, 0, 0]} />
            <Bar dataKey="them" name="Competitor" fill={COLORS.textLight} radius={[3, 3, 0, 0]} />
          </>
        ) : data[0]?.target !== undefined ? (
          <>
            <Bar dataKey="value" name="Actual" fill={COLORS.primary} radius={[3, 3, 0, 0]} />
            <Bar dataKey="target" name="Target" fill={COLORS.primaryLight} radius={[3, 3, 0, 0]} />
          </>
        ) : (
          <Bar dataKey="value" radius={[3, 3, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill || CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Bar>
        )}
        <Legend wrapperStyle={{ fontSize: '9px' }} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ==================== SLIDE COMPONENTS ====================

// Cover slide with professional gradient
const CoverSlide = ({ slide }) => (
  <div className="h-full flex flex-col relative overflow-hidden">
    {/* Gradient background */}
    <div 
      className="absolute inset-0"
      style={{ 
        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`
      }}
    />
    
    {/* Decorative elements */}
    <div 
      className="absolute top-0 right-0 w-1/2 h-full opacity-10"
      style={{ 
        background: `radial-gradient(circle at 80% 20%, ${COLORS.accent} 0%, transparent 50%)`
      }}
    />
    
    {/* Content */}
    <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center px-12">
      {/* Accent line */}
      <div className="w-20 h-1 mb-8" style={{ backgroundColor: COLORS.accent }} />
      
      <h1 
        className="font-bold text-white leading-tight mb-4"
        style={{ fontFamily: 'system-ui', fontSize: 'clamp(24px, 3.5vw, 42px)' }}
      >
        {slide.title}
      </h1>
      
      <p 
        className="text-white/80 mb-8"
        style={{ fontFamily: 'system-ui', fontSize: 'clamp(14px, 1.8vw, 20px)' }}
      >
        {slide.subtitle}
      </p>
      
      {/* Date */}
      <p className="text-white/60 text-sm mt-auto mb-8">
        {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </p>
    </div>
    
    {/* Footer bar */}
    <div 
      className="relative z-10 h-2"
      style={{ background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.primaryLight}, ${COLORS.orange})` }}
    />
  </div>
);

// Section divider slide
const SectionSlide = ({ slide }) => (
  <div className="h-full flex flex-col relative overflow-hidden">
    <div 
      className="absolute inset-0"
      style={{ backgroundColor: COLORS.primaryDark }}
    />
    
    {/* Decorative arrow */}
    <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-20">
      <ArrowRight size={120} color={COLORS.accent} />
    </div>
    
    <div className="relative z-10 flex-1 flex flex-col justify-center px-12">
      {/* Accent line */}
      <div className="w-16 h-1 mb-6" style={{ backgroundColor: COLORS.primaryLight }} />
      
      <h2 
        className="text-white font-bold leading-tight"
        style={{ fontFamily: 'system-ui', fontSize: 'clamp(22px, 3vw, 36px)' }}
      >
        {slide.title}
      </h2>
      
      <div className="w-12 h-1 mt-6" style={{ backgroundColor: COLORS.accent }} />
    </div>
    
    {/* Footer gradient */}
    <div 
      className="relative z-10 h-1"
      style={{ background: `linear-gradient(90deg, ${COLORS.primaryLight}, ${COLORS.accent})` }}
    />
  </div>
);

// Professional header bar
const SlideHeader = ({ title }) => (
  <div 
    className="px-6 py-3 flex items-center gap-4"
    style={{ backgroundColor: COLORS.primary }}
  >
    <h3 
      className="text-white font-semibold flex-1"
      style={{ fontFamily: 'system-ui', fontSize: 'clamp(13px, 1.6vw, 18px)' }}
    >
      {title}
    </h3>
  </div>
);

// Professional footer
const SlideFooter = ({ pageNum }) => (
  <div 
    className="px-6 py-2 flex items-center justify-between"
    style={{ backgroundColor: COLORS.bgLight }}
  >
    <div className="flex items-center gap-2">
      <div className="w-6 h-0.5" style={{ backgroundColor: COLORS.primary }} />
      <div className="w-4 h-0.5" style={{ backgroundColor: COLORS.primaryLight }} />
      <div className="w-2 h-0.5" style={{ backgroundColor: COLORS.accent }} />
    </div>
    <span className="text-xs" style={{ color: COLORS.textLight }}>
      CONFIDENTIAL | {pageNum}
    </span>
  </div>
);

// KPI Card component
const KPICard = ({ value, label, color, trend }) => (
  <div 
    className="rounded-lg p-3 text-center"
    style={{ backgroundColor: color }}
  >
    <div className="flex items-center justify-center gap-1 mb-1">
      <span 
        className="font-bold text-white"
        style={{ fontSize: 'clamp(16px, 2vw, 24px)' }}
      >
        {value}
      </span>
      {trend === 'up' && <TrendingUp size={14} className="text-white" />}
      {trend === 'down' && <TrendingDown size={14} className="text-white" />}
    </div>
    <span className="text-white/90 text-xs">{label}</span>
  </div>
);

// Content slide with smart layout
const ContentSlide = ({ slide, pageNum }) => {
  const hasTable = slide.tables?.length > 0;
  const hasChart = slide.charts?.length > 0;
  const hasKPIs = slide.kpis?.length > 0 || slide.items.filter(i => i.metrics?.value).length >= 3;
  
  if (hasTable) return <TableSlide slide={slide} pageNum={pageNum} />;
  if (hasKPIs) return <KPISlide slide={slide} pageNum={pageNum} />;
  if (hasChart) return <ChartSlide slide={slide} pageNum={pageNum} />;
  
  return <AnalysisSlide slide={slide} pageNum={pageNum} />;
};

// Analysis slide with two-column layout
const AnalysisSlide = ({ slide, pageNum }) => {
  const keyPoints = slide.items.filter(i => i.isKeyPoint || i.type === 'label').slice(0, 4);
  const bullets = slide.items.filter(i => i.type === 'bullet' && !i.isKeyPoint);
  const charts = slide.charts || [];
  
  return (
    <div className="h-full flex flex-col">
      <SlideHeader title={slide.title} />
      
      <div className="flex-1 px-5 py-4 overflow-hidden">
        <div className="h-full grid grid-cols-2 gap-5">
          {/* Left: Key Points */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} style={{ color: COLORS.primary }} />
              <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.primary }}>
                Key Insights
              </h4>
            </div>
            
            <div className="flex flex-col gap-2">
              {(keyPoints.length > 0 ? keyPoints : bullets.slice(0, 4)).map((item, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded"
                  style={{ 
                    backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '15',
                    borderLeft: `3px solid ${CHART_COLORS[idx % CHART_COLORS.length]}`
                  }}
                >
                  {item.metrics?.value && (
                    <div 
                      className="font-bold mb-1"
                      style={{ color: CHART_COLORS[idx % CHART_COLORS.length], fontSize: '16px' }}
                    >
                      {item.metrics.value}
                    </div>
                  )}
                  <p className="text-xs leading-relaxed" style={{ color: COLORS.textDark }}>
                    {item.text.replace(item.metrics?.value || '', '').slice(0, 100).trim()}
                  </p>
                </div>
              ))}
            </div>
            
            {charts.length > 0 && (
              <div className="mt-3 flex-1 min-h-[100px]">
                <SmartChart description={charts[0]} height={100} />
              </div>
            )}
          </div>
          
          {/* Right: Details */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Layers size={14} style={{ color: COLORS.textMedium }} />
              <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.textMedium }}>
                Details & Analysis
              </h4>
            </div>
            
            <div className="flex flex-col gap-1.5">
              {bullets.slice(keyPoints.length > 0 ? 0 : 4, 8).map((item, idx) => (
                <div key={idx} className="flex items-start gap-2" style={{ paddingLeft: item.level ? 16 : 0 }}>
                  <span 
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: item.level ? COLORS.primaryLight : COLORS.primary }}
                  />
                  <p className="text-xs leading-relaxed" style={{ color: COLORS.textDark }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <SlideFooter pageNum={pageNum} />
    </div>
  );
};

// Table slide
const TableSlide = ({ slide, pageNum }) => {
  const table = slide.tables[0] || [];
  const headers = table[0] || [];
  const rows = table.slice(1) || [];
  
  return (
    <div className="h-full flex flex-col">
      <SlideHeader title={slide.title} />
      
      <div className="flex-1 px-5 py-3 overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th 
                  key={i}
                  className="px-2 py-2 text-left text-white font-semibold"
                  style={{ 
                    backgroundColor: COLORS.primary,
                    borderRight: i < headers.length - 1 ? `1px solid ${COLORS.primaryLight}` : 'none'
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((row, ri) => (
              <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? COLORS.bgWhite : COLORS.bgLight }}>
                {row.map((cell, ci) => {
                  const isMetric = /\d+%|\$[\d,]+/.test(cell);
                  const isFirst = ci === 0;
                  return (
                    <td 
                      key={ci}
                      className="px-2 py-1.5"
                      style={{ 
                        borderBottom: `1px solid ${COLORS.border}`,
                        color: isMetric ? COLORS.primary : COLORS.textDark,
                        fontWeight: isFirst || isMetric ? '600' : '400',
                        backgroundColor: isFirst ? COLORS.primary + '08' : 'inherit'
                      }}
                    >
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <SlideFooter pageNum={pageNum} />
    </div>
  );
};

// KPI slide
const KPISlide = ({ slide, pageNum }) => {
  const kpis = slide.kpis?.length > 0 
    ? slide.kpis 
    : slide.items.filter(i => i.metrics?.value).map(i => ({ 
        value: i.metrics.value, 
        label: i.text.replace(i.metrics.value, '').slice(0, 30).trim(),
        trend: i.metrics.trend
      }));
  const bullets = slide.items.filter(i => i.type === 'bullet' && !i.metrics?.value).slice(0, 4);
  const charts = slide.charts || [];
  
  return (
    <div className="h-full flex flex-col">
      <SlideHeader title={slide.title} />
      
      <div className="flex-1 px-5 py-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {kpis.slice(0, 4).map((kpi, idx) => (
            <KPICard 
              key={idx}
              value={kpi.value}
              label={kpi.label}
              color={CHART_COLORS[idx % CHART_COLORS.length]}
              trend={kpi.trend}
            />
          ))}
        </div>
        
        {/* Chart and details */}
        <div className="flex-1 grid grid-cols-2 gap-4">
          <div>
            {charts.length > 0 ? (
              <SmartChart description={charts[0]} height={130} />
            ) : (
              <div className="flex flex-col gap-1.5">
                <h4 className="text-xs font-bold uppercase mb-2" style={{ color: COLORS.textMedium }}>
                  Key Drivers
                </h4>
                {bullets.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5" style={{ backgroundColor: COLORS.primary }} />
                    <p className="text-xs" style={{ color: COLORS.textDark }}>{item.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-1.5">
            <h4 className="text-xs font-bold uppercase mb-2" style={{ color: COLORS.textMedium }}>
              Analysis
            </h4>
            {bullets.map((item, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full mt-1.5" style={{ backgroundColor: COLORS.primaryLight }} />
                <p className="text-xs" style={{ color: COLORS.textDark }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <SlideFooter pageNum={pageNum} />
    </div>
  );
};

// Chart slide
const ChartSlide = ({ slide, pageNum }) => {
  const chart = slide.charts[0];
  const bullets = slide.items.filter(i => i.type === 'bullet').slice(0, 4);
  
  return (
    <div className="h-full flex flex-col">
      <SlideHeader title={slide.title} />
      
      <div className="flex-1 px-5 py-4">
        <div className="h-full grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-bold uppercase mb-3" style={{ color: COLORS.primary }}>
              <BarChart3 size={12} className="inline mr-1" />
              Visualization
            </h4>
            <SmartChart description={chart} height={180} />
          </div>
          
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-bold uppercase mb-2" style={{ color: COLORS.textMedium }}>
              Key Takeaways
            </h4>
            {bullets.map((item, idx) => (
              <div 
                key={idx}
                className="p-2 rounded"
                style={{ 
                  backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '10',
                  borderLeft: `2px solid ${CHART_COLORS[idx % CHART_COLORS.length]}`
                }}
              >
                <p className="text-xs" style={{ color: COLORS.textDark }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <SlideFooter pageNum={pageNum} />
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const ProfessionalSlidePreview = ({ content, title, onClose, onDownloadPDF, onDownloadPPT }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = useMemo(() => parseContentToSlides(content || '', title), [content, title]);
  const totalSlides = slides.length;
  
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);
  
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  }, []);
  
  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, onClose]);
  
  const slide = slides[currentSlide];
  
  const renderSlide = () => {
    if (!slide) return null;
    const pageNum = currentSlide + 1;
    
    switch (slide.type) {
      case 'cover':
        return <CoverSlide slide={slide} />;
      case 'section':
        return slide.items.length === 0 && !slide.tables?.length
          ? <SectionSlide slide={slide} />
          : <ContentSlide slide={slide} pageNum={pageNum} />;
      default:
        return <ContentSlide slide={slide} pageNum={pageNum} />;
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: COLORS.primaryDark + 'f5' }}
      onClick={onClose}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-5 py-2"
        style={{ backgroundColor: COLORS.primary }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-white font-semibold text-sm">{title}</h2>
          <span 
            className="text-xs px-2 py-0.5 rounded font-medium"
            style={{ backgroundColor: COLORS.accent, color: COLORS.primaryDark }}
          >
            {currentSlide + 1} / {totalSlides}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={onDownloadPDF}
            className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium hover:scale-105 transition-transform"
            style={{ backgroundColor: COLORS.bgWhite, color: COLORS.textDark }}
          >
            <FileType size={14} /> PDF
          </button>
          <button 
            onClick={onDownloadPPT}
            className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium hover:scale-105 transition-transform"
            style={{ backgroundColor: COLORS.bgWhite, color: COLORS.textDark }}
          >
            <Presentation size={14} /> PPT
          </button>
          <button 
            onClick={onClose}
            className="text-white hover:text-[#9ED2F3] p-1 ml-1"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      {/* Slide */}
      <div 
        className="flex-1 flex items-center justify-center p-4"
        onClick={e => e.stopPropagation()}
      >
        <div 
          className="bg-white rounded-lg shadow-2xl overflow-hidden"
          style={{ 
            width: '100%', 
            maxWidth: '960px',
            aspectRatio: '16/9'
          }}
        >
          {renderSlide()}
        </div>
      </div>
      
      {/* Navigation */}
      <div 
        className="flex items-center justify-center gap-4 py-2"
        style={{ backgroundColor: COLORS.primary }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="p-2 rounded-full transition-all disabled:opacity-30 hover:scale-110"
          style={{ backgroundColor: COLORS.bgWhite }}
        >
          <ChevronLeft size={16} style={{ color: COLORS.primary }} />
        </button>
        
        <div className="flex gap-1.5 items-center">
          {slides.slice(0, 12).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className="transition-all hover:scale-110"
              style={{
                width: idx === currentSlide ? '18px' : '6px',
                height: '6px',
                borderRadius: '3px',
                backgroundColor: idx === currentSlide ? COLORS.accent : 'rgba(255,255,255,0.3)'
              }}
            />
          ))}
          {slides.length > 12 && (
            <span className="text-white/50 text-xs ml-1">+{slides.length - 12}</span>
          )}
        </div>
        
        <button 
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          className="p-2 rounded-full transition-all disabled:opacity-30 hover:scale-110"
          style={{ backgroundColor: COLORS.bgWhite }}
        >
          <ChevronRight size={16} style={{ color: COLORS.primary }} />
        </button>
      </div>
      
      <div className="text-center pb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
        <span className="text-xs">← → to navigate • ESC to close</span>
      </div>
    </motion.div>
  );
};

export default ProfessionalSlidePreview;
